import { z } from 'zod';

import { AssetRef, HexColor } from './primitives.js';
import { Tile } from './tiles.js';

/**
 * Theme palette. Schema accepts every field at every tier; runtime honors only
 * `background`, `primary`, and `accent` on Core. Pro tier (theming flag)
 * unlocks the full palette plus logo, wallpaper, custom font, and dark mode.
 */
/**
 * Legacy hero-style enum — retained so configs from before the
 * `profile.hero` discriminated union still parse. The renderer ignores
 * this field at render time; use the new `Hero` union below for any
 * new work.
 */
export const HeroStyle = z.enum(['cards', 'status', 'identity', 'wallpaper']);
export type HeroStyle = z.infer<typeof HeroStyle>;

/**
 * Hero band variants — what fills the slab between the header and the
 * tile grid. Discriminated union by `kind`. Honored at every tier
 * (it's layout, not a Pro upsell).
 *
 *   featured  — up to 4 large tiles from profile.featuredTiles (legacy
 *               default; equivalent to leaving `hero` unset)
 *   clock     — large time display, optional date + caption line
 *   weather   — clock + weather card via Open-Meteo (free, no key);
 *               fixed lat/lng or browser geolocation
 *   richText  — Tiptap-authored content (headings, lists, emphasis)
 *               serialized as a ProseMirror JSON string
 *   none      — no hero; the tile grid expands to fill the viewport
 */
const HeroFeatured = z.object({ kind: z.literal('featured') });
const HeroClock = z.object({
  kind: z.literal('clock'),
  showDate: z.boolean().optional(),
  caption: z.string().max(120).optional(),
});
const HeroWeather = z.object({
  kind: z.literal('weather'),
  showDate: z.boolean().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  locationLabel: z.string().max(60).optional(),
});
const HeroRichText = z.object({
  kind: z.literal('richText'),
  /**
   * Tiptap ProseMirror JSON serialized as a string. Stored as a string
   * (not a nested object) so this schema doesn't need to track Tiptap's
   * evolving JSON shape — the Builder validates structure before save
   * by round-tripping through Tiptap's parse / generateHTML.
   */
  contentJson: z.string().min(2).max(20_000),
});
const HeroNone = z.object({ kind: z.literal('none') });

export const Hero = z.discriminatedUnion('kind', [
  HeroFeatured,
  HeroClock,
  HeroWeather,
  HeroRichText,
  HeroNone,
]);
export type Hero = z.infer<typeof Hero>;

/**
 * Rich palette — every user-themable surface on the launcher in one
 * block. Every field is optional so a customer can paint just one
 * surface (e.g. "make the header navy") without having to fill in
 * every other color too. The renderer falls back through the existing
 * flat ProTheme fields and then the product defaults from
 * styles/index.css.
 *
 * Used twice in ProTheme: `paletteLight` (active when data-theme
 * resolves to light) and `paletteDark` (active when dark). The base
 * flat fields (background/surface/text/...) remain on ProTheme as the
 * default light values for back-compat; configs from before the
 * dual-palette feature keep working without changes.
 */
export const Palette = z.object({
  // base
  background: HexColor.optional(),
  surface: HexColor.optional(),
  primary: HexColor.optional(),
  accent: HexColor.optional(),
  text: HexColor.optional(),
  textMuted: HexColor.optional(),
  // chrome surfaces
  headerBg: HexColor.optional(),
  headerInk: HexColor.optional(),
  footerBg: HexColor.optional(),
  heroBg: HexColor.optional(),
  // tile + card surfaces
  tileBg: HexColor.optional(),
  tileBorder: HexColor.optional(),
  cardBg: HexColor.optional(),
  // accents
  cta: HexColor.optional(),
  iconAccent: HexColor.optional(),
  divider: HexColor.optional(),
});
export type Palette = z.infer<typeof Palette>;

export const ProTheme = z.object({
  background: HexColor,
  primary: HexColor,
  accent: HexColor,
  surface: HexColor.optional(),
  text: HexColor.optional(),
  textMuted: HexColor.optional(),
  /**
   * Optional light-mode palette overrides — fills in the surfaces that
   * the flat fields above don't cover (header / footer / hero / tile
   * accents / etc.). The flat fields stay populated for back-compat,
   * so configs without this block keep their current paint.
   */
  paletteLight: Palette.optional(),
  /**
   * Dark-mode palette — applied when data-theme is `always` OR when
   * data-theme is `auto` AND prefers-color-scheme: dark. Falls back
   * through the renderer's hardcoded dark defaults for surfaces the
   * customer didn't override.
   */
  paletteDark: Palette.optional(),
  logoRef: AssetRef.optional(),
  /**
   * Inline logo image as a base64 data URL — set by the Portal's
   * Branding tab drop-zone uploader. The renderer prefers this over
   * `logoRef` when both are set. Capped at ~500 KB after encoding so
   * the config blob doesn't bloat with full-size brand artwork
   * (256×256 is the recommended source; the uploader downscales).
   */
  logoDataUrl: z
    .string()
    .startsWith('data:image/')
    .max(500_000)
    .optional(),
  wallpaperRef: AssetRef.optional(),
  /**
   * Inline wallpaper image as a base64 data URL — set by the Portal's
   * drop-and-upload flow for self-service tenants who don't have a
   * separate asset-hosting pipeline. The renderer prefers this over
   * `wallpaperRef` when both are set.
   *
   * Capped at ~3 MB after base64 encoding (~2.2 MB raw image) so the
   * config blob stays reasonable to ship to every device on every poll.
   * The Portal's uploader downscales above 1920×1080 before encoding.
   */
  wallpaperDataUrl: z
    .string()
    .startsWith('data:image/')
    .max(3_000_000)
    .optional(),
  /**
   * Optional image for the launcher's hero band. When set (Pro/Ent
   * with the `theming` flag), replaces the brand-color hero
   * background. Distinct from `wallpaperRef`, which still applies to
   * the whole launcher's main area — use both to layer.
   */
  heroImageRef: AssetRef.optional(),
  fontRef: AssetRef.optional(),
  darkMode: z.enum(['auto', 'always', 'never']).optional(),
  heroStyle: HeroStyle.optional(),
});
export type ProTheme = z.infer<typeof ProTheme>;

/**
 * Top status bar. `showCurrentUser` is Enterprise-only (ssoDisplay flag).
 * Other booleans render at every tier.
 */
export const StatusBar = z.object({
  visible: z.boolean(),
  showClock: z.boolean().optional(),
  showBattery: z.boolean().optional(),
  showSignal: z.boolean().optional(),
  showCurrentUser: z.boolean().optional(),
});
export type StatusBar = z.infer<typeof StatusBar>;

/**
 * Tile grid. Tiles fill cells in row-major order; `spacer` tiles produce
 * blanks. Length must not exceed `columns × rows`. Min 1 tile (an empty
 * grid is almost certainly a misconfiguration; we surface it loudly).
 */
export const Grid = z
  .object({
    columns: z.number().int().min(3).max(8),
    rows: z.number().int().min(3).max(12),
    tiles: z.array(Tile).min(1),
  })
  .refine((g) => g.tiles.length <= g.columns * g.rows, {
    message: 'tiles.length must not exceed columns × rows',
    path: ['tiles'],
  });
export type Grid = z.infer<typeof Grid>;
